import { useCallback, useEffect, useMemo, useState } from "react";
import {
  disableFoodRetailSource,
  enableFoodRetailSource,
  fetchFoodSourceDiscovery,
  fetchFoodRetailIngestionReadiness,
  fetchFoodRetailIngestionRuns,
  fetchFoodRetailSourceEnablementStatus,
  fetchLatestFoodRetailPrices,
  runFoodRetailIngestion,
} from "../../../../api/foodApi";
import { statusBadgeClass } from "../../../../utils/foodFormatters";
import SourceHealthBadge from "../SourceHealthBadge";
import AdminTableContainer from "../layout/AdminTableContainer";
import {
  FOOD_SOURCE_KEYS,
  isFoodRetailPlaybookSource,
} from "../../../../constants/foodSourceConstants";
import {
  canDisableSource,
  canEnableSource,
  canRunIngestion,
  filterRetailSupermarketSources,
  formatBool,
  formatCopPrice,
  formatDateTime,
  getDisableDisabledReason,
  getEnableDisabledReason,
  getEnablementBanner,
  getReadinessBanner,
  getRunDisabledReason,
  getRunResultMessage,
  formatBlockingReason,
  parseRetailIngestionApiError,
  RETAIL_INGESTION_LIMITS,
  validateRunForm,
} from "./foodRetailIngestionHelpers";
import FoodRetailNormalizationSection from "./FoodRetailNormalizationSection";
import FoodAraCompleteSustainabilitySection from "./FoodAraCompleteSustainabilitySection";
import FoodRetailManualRefreshPlaybookSection from "./FoodRetailManualRefreshPlaybookSection";
import FoodAraBasketCalculationSection from "./FoodAraBasketCalculationSection";
import FoodAraBasketCoverageBaselineSection from "./FoodAraBasketCoverageBaselineSection";
import FoodAraStapleTargetPlanSection from "./FoodAraStapleTargetPlanSection";
import FoodAraCityGateStrategySection from "./FoodAraCityGateStrategySection";
import FoodAraIngestionExpansionSection from "./FoodAraIngestionExpansionSection";
import FoodAraPackageReadinessSection from "./FoodAraPackageReadinessSection";
import FoodAraFreshnessSection from "./FoodAraFreshnessSection";
import FoodReviewedLiveUrlsSection from "./FoodReviewedLiveUrlsSection";
import {
  loadReviewedLiveCategorySource,
  validateSelectedLiveCategoryUrl,
} from "./foodReviewedLiveUrlsHelpers";

const FoodRetailIngestionPage = ({ onClose }) => {
  const [sources, setSources] = useState([]);
  const [selectedSourceKey, setSelectedSourceKey] = useState("");
  const [readiness, setReadiness] = useState(null);
  const [enablement, setEnablement] = useState(null);
  const [runs, setRuns] = useState([]);
  const [observations, setObservations] = useState([]);
  const [dryRun, setDryRun] = useState(true);
  const [useMock, setUseMock] = useState(false);
  const [maxCategories, setMaxCategories] = useState(
    RETAIL_INGESTION_LIMITS.DEFAULT_MAX_CATEGORIES
  );
  const [maxProductsPerCategory, setMaxProductsPerCategory] = useState(
    RETAIL_INGESTION_LIMITS.DEFAULT_MAX_PRODUCTS_PER_CATEGORY
  );
  const [categoryUrl, setCategoryUrl] = useState("");
  const [approvedLiveUrls, setApprovedLiveUrls] = useState([]);
  const [liveCategorySource, setLiveCategorySource] = useState(null);
  const [realRunConfirmed, setRealRunConfirmed] = useState(false);
  const [enableConfirmed, setEnableConfirmed] = useState(false);
  const [disableConfirmed, setDisableConfirmed] = useState(false);
  const [disableReason, setDisableReason] = useState("");
  const [loadingSources, setLoadingSources] = useState(true);
  const [loadingReadiness, setLoadingReadiness] = useState(false);
  const [loadingEnablement, setLoadingEnablement] = useState(false);
  const [loadingRuns, setLoadingRuns] = useState(false);
  const [loadingObservations, setLoadingObservations] = useState(false);
  const [running, setRunning] = useState(false);
  const [enabling, setEnabling] = useState(false);
  const [disabling, setDisabling] = useState(false);
  const [runResult, setRunResult] = useState(null);
  const [enablementMessage, setEnablementMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const retailSources = useMemo(() => filterRetailSupermarketSources(sources), [sources]);

  const selectedSource = useMemo(
    () => retailSources.find((source) => source.sourceKey === selectedSourceKey) || null,
    [retailSources, selectedSourceKey]
  );

  const liveUrlValidation = useMemo(
    () =>
      validateSelectedLiveCategoryUrl({
        categoryUrl,
        approvedUrls: approvedLiveUrls,
        sourcePayload: liveCategorySource,
      }),
    [categoryUrl, approvedLiveUrls, liveCategorySource]
  );

  const formErrors = useMemo(
    () =>
      validateRunForm({
        maxCategories,
        maxProductsPerCategory,
        categoryUrl,
        liveUrlValidation,
      }),
    [maxCategories, maxProductsPerCategory, categoryUrl, liveUrlValidation]
  );

  const runDisabledReason = useMemo(
    () =>
      getRunDisabledReason({
        sourceKey: selectedSourceKey,
        readiness,
        dryRun,
        realRunConfirmed,
        formErrors,
        running,
      }),
    [selectedSourceKey, readiness, dryRun, realRunConfirmed, formErrors, running]
  );

  const runAllowed = canRunIngestion({
    sourceKey: selectedSourceKey,
    readiness,
    dryRun,
    realRunConfirmed,
    formErrors,
    running,
  });

  const enableDisabledReason = useMemo(
    () =>
      getEnableDisabledReason({
        sourceKey: selectedSourceKey,
        enablement,
        enableConfirmed,
        enabling,
      }),
    [selectedSourceKey, enablement, enableConfirmed, enabling]
  );

  const disableDisabledReason = useMemo(
    () =>
      getDisableDisabledReason({
        sourceKey: selectedSourceKey,
        enablement,
        disableConfirmed,
        disabling,
      }),
    [selectedSourceKey, enablement, disableConfirmed, disabling]
  );

  const enableAllowed = canEnableSource({
    sourceKey: selectedSourceKey,
    enablement,
    enableConfirmed,
    enabling,
  });

  const disableAllowed = canDisableSource({
    sourceKey: selectedSourceKey,
    enablement,
    disableConfirmed,
    disabling,
  });

  const loadSources = useCallback(async () => {
    try {
      setLoadingSources(true);
      setErrorMessage("");
      const data = await fetchFoodSourceDiscovery();
      const retail = filterRetailSupermarketSources(data.sources || []);
      setSources(data.sources || []);
      setSelectedSourceKey((current) => {
        if (current && retail.some((source) => source.sourceKey === current)) {
          return current;
        }
        return retail[0]?.sourceKey || "";
      });
    } catch (error) {
      setSources([]);
      setErrorMessage(parseRetailIngestionApiError(error));
    } finally {
      setLoadingSources(false);
    }
  }, []);

  const loadReviewedUrls = useCallback(async (sourceKey) => {
    if (!sourceKey) {
      setApprovedLiveUrls([]);
      setLiveCategorySource(null);
      setCategoryUrl("");
      return;
    }

    try {
      const { sourcePayload, approvedUrls } = await loadReviewedLiveCategorySource(sourceKey);
      setLiveCategorySource(sourcePayload);
      setApprovedLiveUrls(approvedUrls);
      setCategoryUrl((current) => {
        if (current && approvedUrls.some((entry) => entry.url === current)) {
          return current;
        }
        return approvedUrls[0]?.url || "";
      });
    } catch {
      setApprovedLiveUrls([]);
      setLiveCategorySource(null);
      setCategoryUrl("");
    }
  }, []);

  useEffect(() => {
    loadReviewedUrls(selectedSourceKey);
  }, [selectedSourceKey, loadReviewedUrls]);

  const loadReadiness = useCallback(async (sourceKey) => {
    if (!sourceKey) {
      setReadiness(null);
      return;
    }

    try {
      setLoadingReadiness(true);
      setReadiness(await fetchFoodRetailIngestionReadiness(sourceKey));
    } catch (error) {
      setReadiness(null);
      setErrorMessage(parseRetailIngestionApiError(error));
    } finally {
      setLoadingReadiness(false);
    }
  }, []);

  const loadEnablement = useCallback(async (sourceKey) => {
    if (!sourceKey) {
      setEnablement(null);
      return;
    }

    try {
      setLoadingEnablement(true);
      setEnablement(await fetchFoodRetailSourceEnablementStatus(sourceKey));
    } catch (error) {
      setEnablement(null);
      setErrorMessage(parseRetailIngestionApiError(error));
    } finally {
      setLoadingEnablement(false);
    }
  }, []);

  const loadRuns = useCallback(async (sourceKey) => {
    if (!sourceKey) {
      setRuns([]);
      return;
    }

    try {
      setLoadingRuns(true);
      const data = await fetchFoodRetailIngestionRuns({ sourceKey, limit: 25 });
      setRuns(data.runs || []);
    } catch (error) {
      setRuns([]);
      setErrorMessage(parseRetailIngestionApiError(error));
    } finally {
      setLoadingRuns(false);
    }
  }, []);

  const loadObservations = useCallback(async (sourceKey) => {
    if (!sourceKey) {
      setObservations([]);
      return;
    }

    try {
      setLoadingObservations(true);
      const data = await fetchLatestFoodRetailPrices({ sourceKey, limit: 25 });
      setObservations(data.observations || []);
    } catch (error) {
      setObservations([]);
      setErrorMessage(parseRetailIngestionApiError(error));
    } finally {
      setLoadingObservations(false);
    }
  }, []);

  const refreshSourcePanels = useCallback(
    async (sourceKey) => {
      await Promise.all([
        loadReadiness(sourceKey),
        loadEnablement(sourceKey),
        loadRuns(sourceKey),
        loadObservations(sourceKey),
      ]);
    },
    [loadReadiness, loadEnablement, loadRuns, loadObservations]
  );

  useEffect(() => {
    loadSources();
  }, [loadSources]);

  useEffect(() => {
    if (!selectedSourceKey) return;
    refreshSourcePanels(selectedSourceKey);
  }, [selectedSourceKey, refreshSourcePanels]);

  useEffect(() => {
    if (dryRun) {
      setRealRunConfirmed(false);
    }
  }, [dryRun]);

  useEffect(() => {
    setEnableConfirmed(false);
    setDisableConfirmed(false);
    setDisableReason("");
    setEnablementMessage("");
  }, [selectedSourceKey]);

  const handleEvaluateEnablement = async () => {
    if (!selectedSourceKey) return;
    setEnablementMessage("");
    await loadEnablement(selectedSourceKey);
  };

  const handleEnableSource = async () => {
    if (!enableAllowed || !selectedSourceKey) return;

    try {
      setEnabling(true);
      setErrorMessage("");
      setEnablementMessage("");
      const result = await enableFoodRetailSource(selectedSourceKey, {
        confirm: true,
        enabledBy: "admin",
        notes: "Enabled after retail ingestion PoC validation.",
      });
      setEnablementMessage(result.message || `${selectedSourceKey} enabled.`);
      await Promise.all([loadSources(), refreshSourcePanels(selectedSourceKey)]);
    } catch (error) {
      setEnablementMessage("");
      setErrorMessage(parseRetailIngestionApiError(error));
    } finally {
      setEnabling(false);
    }
  };

  const handleDisableSource = async () => {
    if (!disableAllowed || !selectedSourceKey) return;

    try {
      setDisabling(true);
      setErrorMessage("");
      setEnablementMessage("");
      const result = await disableFoodRetailSource(selectedSourceKey, {
        confirm: true,
        disabledBy: "admin",
        reason: disableReason.trim(),
      });
      setEnablementMessage(result.message || `${selectedSourceKey} disabled.`);
      await Promise.all([loadSources(), refreshSourcePanels(selectedSourceKey)]);
    } catch (error) {
      setEnablementMessage("");
      setErrorMessage(parseRetailIngestionApiError(error));
    } finally {
      setDisabling(false);
    }
  };

  const handleRun = async () => {
    if (!runAllowed || !selectedSourceKey) return;

    try {
      setRunning(true);
      setErrorMessage("");
      setRunResult(null);

      const payload = {
        sourceKey: selectedSourceKey,
        dryRun,
        useMock,
        maxCategories: Number(maxCategories),
        maxProductsPerCategory: Number(maxProductsPerCategory),
        categoryUrls: categoryUrl.trim() ? [categoryUrl.trim()] : [],
      };

      const result = await runFoodRetailIngestion(payload);
      setRunResult(result);
      await refreshSourcePanels(selectedSourceKey);
    } catch (error) {
      setRunResult(null);
      setErrorMessage(parseRetailIngestionApiError(error));
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="admin-page food-retail-ingestion-page">
      <header className="admin-page__header">
        <div>
          <p className="admin-page__eyebrow">Food Data</p>
          <h1>Food Retail Ingestion</h1>
          <p className="muted">
            Use this page to manually test supermarket retail price ingestion. Start with dry runs.
            Only run real ingestion for sources that have confirmed public price availability and
            acceptable discovery status.
          </p>
        </div>
        {onClose ? (
          <button type="button" className="btn btn--ghost" onClick={onClose}>
            Close
          </button>
        ) : null}
      </header>

      {errorMessage ? <p className="food-dashboard__error">{errorMessage}</p> : null}

      <section className="panel-card food-retail-ingestion-page__sources">
        <div className="food-retail-ingestion-page__section-header">
          <h2>Retail supermarket sources</h2>
          <button type="button" className="btn btn--ghost btn--sm" onClick={loadSources}>
            Refresh sources
          </button>
        </div>

        {loadingSources ? <p className="muted">Loading sources…</p> : null}

        <AdminTableContainer>
          <table className="admin-table">
            <thead>
              <tr>
                <th>Source</th>
                <th>Discovery</th>
                <th>Health</th>
                <th>Enabled</th>
                <th>Public price</th>
                <th>Login</th>
                <th>App only</th>
                <th>Access</th>
                <th>Terms</th>
                <th>Risk</th>
                <th>Last success</th>
                <th>Last failure</th>
              </tr>
            </thead>
            <tbody>
              {retailSources.map((source) => (
                <tr
                  key={source.sourceKey}
                  className={
                    source.sourceKey === selectedSourceKey
                      ? "food-retail-ingestion-page__row--selected"
                      : ""
                  }
                  onClick={() => setSelectedSourceKey(source.sourceKey)}
                >
                  <td>
                    <strong>{source.sourceName}</strong>
                    <div className="muted food-retail-ingestion-page__mono">{source.sourceKey}</div>
                  </td>
                  <td>{source.discoveryStatus || "—"}</td>
                  <td>
                    <SourceHealthBadge status={source.healthStatus} />
                  </td>
                  <td>{formatBool(source.enabled)}</td>
                  <td>{formatBool(source.publicPriceAvailable)}</td>
                  <td>{formatBool(source.loginRequired)}</td>
                  <td>{formatBool(source.appOnly)}</td>
                  <td>{source.recommendedAccessMethod || "—"}</td>
                  <td>{source.termsReviewStatus || "—"}</td>
                  <td>{source.ingestionRiskLevel || "—"}</td>
                  <td>{formatDateTime(source.lastSuccessAt)}</td>
                  <td>{formatDateTime(source.lastFailureAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </AdminTableContainer>
      </section>

      <div className="food-retail-ingestion-page__grid">
        <section className="panel-card">
          <h2>Source readiness</h2>
          {selectedSource ? (
            <p className="muted">
              Selected: <strong>{selectedSource.sourceName}</strong> ({selectedSource.sourceKey})
            </p>
          ) : (
            <p className="muted">Select a retail source from the table above.</p>
          )}

          {loadingReadiness ? <p className="muted">Loading readiness…</p> : null}

          {readiness ? (
            <>
              <div
                className={`food-retail-ingestion-page__banner ${
                  readiness.isRunnable
                    ? "food-retail-ingestion-page__banner--ready"
                    : "food-retail-ingestion-page__banner--blocked"
                }`}
              >
                {getReadinessBanner(readiness, selectedSourceKey)}
              </div>

              <dl className="food-retail-ingestion-page__readiness-grid">
                <dt>Runnable</dt>
                <dd>{formatBool(readiness.isRunnable)}</dd>
                <dt>Dry run allowed</dt>
                <dd>{formatBool(readiness.dryRunAllowed)}</dd>
                <dt>Real run allowed</dt>
                <dd>{formatBool(readiness.realRunAllowed)}</dd>
                <dt>Discovery status</dt>
                <dd>{readiness.discoveryStatus || "—"}</dd>
                <dt>Recommended access</dt>
                <dd>{readiness.recommendedAccessMethod || "—"}</dd>
                <dt>Ingestion risk</dt>
                <dd>{readiness.ingestionRiskLevel || "—"}</dd>
                <dt>Terms review</dt>
                <dd>{readiness.termsReviewStatus || "—"}</dd>
              </dl>

              {readiness.fixtureModeRecommended ? (
                <p className="muted">
                  Verified fixture mode is available for this source
                  {readiness.defaultFixturePath ? ` (${readiness.defaultFixturePath})` : ""}. Live
                  fetch remains blocked unless you supply a reviewed category URL.
                </p>
              ) : null}

              {readiness.blockingReasons?.length ? (
                <div className="food-retail-ingestion-page__blocked">
                  <h3>Blocking reasons</h3>
                  <ul>
                    {readiness.blockingReasons.map((reason) => (
                      <li key={reason}>{formatBlockingReason(reason)}</li>
                    ))}
                  </ul>
                </div>
              ) : null}

              {readiness.warnings?.length ? (
                <div className="food-retail-ingestion-page__warnings">
                  <h3>Warnings</h3>
                  <ul>
                    {readiness.warnings.map((warning) => (
                      <li key={warning}>{warning}</li>
                    ))}
                  </ul>
                </div>
              ) : null}
            </>
          ) : null}
        </section>

        <FoodReviewedLiveUrlsSection sourceKey={selectedSourceKey} />

        <section className="panel-card food-basket-calculation-page__controls">
          <h2>Manual run controls</h2>

          <div className="food-normalization-page__control-row">
            <label className="food-comparison-filters__checkbox">
              <input
                type="checkbox"
                checked={dryRun}
                onChange={(event) => setDryRun(event.target.checked)}
              />
              Dry run (default)
            </label>

            <label className="food-comparison-filters__checkbox">
              <input
                type="checkbox"
                checked={useMock}
                onChange={(event) => setUseMock(event.target.checked)}
              />
              Use generic mock fixture (optional override)
            </label>

            <label>
              Max categories
              <input
                type="number"
                min={1}
                max={RETAIL_INGESTION_LIMITS.MAX_CATEGORIES}
                value={maxCategories}
                onChange={(event) => setMaxCategories(event.target.value)}
              />
            </label>

            <label>
              Max products per category
              <input
                type="number"
                min={1}
                max={RETAIL_INGESTION_LIMITS.MAX_PRODUCTS_PER_CATEGORY}
                value={maxProductsPerCategory}
                onChange={(event) => setMaxProductsPerCategory(event.target.value)}
              />
            </label>

            <label className="food-retail-ingestion-page__url-field">
              Reviewed category URL
              {approvedLiveUrls.length ? (
                <select
                  value={categoryUrl}
                  onChange={(event) => setCategoryUrl(event.target.value)}
                >
                  <option value="">Use fixture/default (no live URL)</option>
                  {approvedLiveUrls.map((entry) => (
                    <option key={entry.url} value={entry.url}>
                      {entry.url}
                    </option>
                  ))}
                </select>
              ) : (
                <input type="text" value="" disabled placeholder="No approved live URLs" />
              )}
            </label>
          </div>

          {liveCategorySource?.liveCategoryMode === "FIXTURE_ONLY" ? (
            <p className="food-retail-ingestion-page__disabled-reason">
              Fixture-only source — live category URLs cannot be submitted.
            </p>
          ) : null}

          {categoryUrl && liveUrlValidation?.valid === false ? (
            <p className="food-retail-ingestion-page__disabled-reason">
              {liveUrlValidation.message}
              {liveUrlValidation.reason ? ` (${liveUrlValidation.reason})` : ""}
            </p>
          ) : null}

          {!dryRun ? (
            <label className="food-comparison-filters__checkbox food-retail-ingestion-page__confirm">
              <input
                type="checkbox"
                checked={realRunConfirmed}
                onChange={(event) => setRealRunConfirmed(event.target.checked)}
              />
              I understand this will insert retail price observations for the selected source.
            </label>
          ) : null}

          {formErrors.length ? (
            <ul className="food-retail-ingestion-page__form-errors">
              {formErrors.map((error) => (
                <li key={error}>{error}</li>
              ))}
            </ul>
          ) : null}

          {runDisabledReason ? (
            <p className="food-retail-ingestion-page__disabled-reason">{runDisabledReason}</p>
          ) : null}

          <button
            type="button"
            className="btn btn--primary"
            disabled={!runAllowed}
            onClick={handleRun}
          >
            {running ? "Running…" : dryRun ? "Run dry ingestion" : "Run real ingestion"}
          </button>
        </section>
      </div>

      <section className="panel-card food-retail-ingestion-page__enablement">
        <div className="food-retail-ingestion-page__section-header">
          <h2>Source enablement</h2>
          <button
            type="button"
            className="btn btn--ghost btn--sm"
            disabled={!selectedSourceKey || loadingEnablement}
            onClick={handleEvaluateEnablement}
          >
            Evaluate enablement
          </button>
        </div>

        <p className="muted">
          Enablement marks a source as ready for regular manual collection. It does not start
          scheduled ingestion.
        </p>

        {enablementMessage ? (
          <p className="food-retail-ingestion-page__banner food-retail-ingestion-page__banner--ready">
            {enablementMessage}
          </p>
        ) : null}

        {loadingEnablement ? <p className="muted">Loading enablement status…</p> : null}

        {enablement ? (
          <>
            <div
              className={`food-retail-ingestion-page__banner ${
                enablement.canEnable || enablement.enabled
                  ? "food-retail-ingestion-page__banner--ready"
                  : "food-retail-ingestion-page__banner--blocked"
              }`}
            >
              {getEnablementBanner(enablement, selectedSourceKey)}
            </div>

            <dl className="food-retail-ingestion-page__readiness-grid">
              <dt>Enabled</dt>
              <dd>{formatBool(enablement.enabled)}</dd>
              <dt>Can enable</dt>
              <dd>{formatBool(enablement.canEnable)}</dd>
              <dt>Discovery status</dt>
              <dd>{enablement.evidence?.discoveryStatus || "—"}</dd>
              <dt>Health status</dt>
              <dd>{enablement.currentHealthStatus || "—"}</dd>
              <dt>Recommended access</dt>
              <dd>{enablement.recommendedAccessMethod || "—"}</dd>
              <dt>Access method</dt>
              <dd>{enablement.accessMethod || "—"}</dd>
              <dt>Last successful dry run</dt>
              <dd>{formatDateTime(enablement.evidence?.lastSuccessfulDryRunAt)}</dd>
              <dt>Last successful real run</dt>
              <dd>{formatDateTime(enablement.evidence?.lastSuccessfulRealRunAt)}</dd>
              <dt>Last observations inserted</dt>
              <dd>{enablement.evidence?.lastObservationsInserted ?? 0}</dd>
            </dl>

            <p className="muted">{enablement.recommendedNextAction}</p>

            {enablement.blockingReasons?.length ? (
              <div className="food-retail-ingestion-page__blocked">
                <h3>Enablement blocking reasons</h3>
                <ul>
                  {enablement.blockingReasons.map((reason) => (
                    <li key={reason}>{reason}</li>
                  ))}
                </ul>
              </div>
            ) : null}

            {enablement.warnings?.length ? (
              <div className="food-retail-ingestion-page__warnings">
                <h3>Enablement warnings</h3>
                <ul>
                  {enablement.warnings.map((warning) => (
                    <li key={warning}>{warning}</li>
                  ))}
                </ul>
              </div>
            ) : null}

            <div className="food-retail-ingestion-page__enablement-actions">
              {!enablement.enabled ? (
                <label className="food-comparison-filters__checkbox food-retail-ingestion-page__confirm">
                  <input
                    type="checkbox"
                    checked={enableConfirmed}
                    onChange={(event) => setEnableConfirmed(event.target.checked)}
                  />
                  I understand this changes the source registry status. It does not start scheduled
                  ingestion.
                </label>
              ) : (
                <>
                  <label className="food-retail-ingestion-page__url-field">
                    Disable reason
                    <input
                      type="text"
                      value={disableReason}
                      placeholder="Reason for disabling this source"
                      onChange={(event) => setDisableReason(event.target.value)}
                    />
                  </label>
                  <label className="food-comparison-filters__checkbox food-retail-ingestion-page__confirm">
                    <input
                      type="checkbox"
                      checked={disableConfirmed}
                      onChange={(event) => setDisableConfirmed(event.target.checked)}
                    />
                    I understand this changes the source registry status. It does not start scheduled
                    ingestion.
                  </label>
                </>
              )}

              {enableDisabledReason && !enablement.enabled ? (
                <p className="food-retail-ingestion-page__disabled-reason">{enableDisabledReason}</p>
              ) : null}

              {disableDisabledReason && enablement.enabled ? (
                <p className="food-retail-ingestion-page__disabled-reason">{disableDisabledReason}</p>
              ) : null}

              <div className="food-retail-ingestion-page__enablement-buttons">
                {!enablement.enabled ? (
                  <button
                    type="button"
                    className="btn btn--primary"
                    disabled={!enableAllowed}
                    onClick={handleEnableSource}
                  >
                    {enabling ? "Enabling…" : "Enable source"}
                  </button>
                ) : (
                  <button
                    type="button"
                    className="btn btn--ghost"
                    disabled={!disableAllowed}
                    onClick={handleDisableSource}
                  >
                    {disabling ? "Disabling…" : "Disable source"}
                  </button>
                )}
              </div>
            </div>
          </>
        ) : null}
      </section>

      {runResult ? (
        <section className="panel-card food-retail-ingestion-page__result">
          <h2>Last run result</h2>
          <p>{getRunResultMessage(runResult)}</p>

          <dl className="food-retail-ingestion-page__result-grid">
            <dt>Status</dt>
            <dd>
              <span className={statusBadgeClass(runResult.status)}>{runResult.status}</span>
            </dd>
            <dt>Dry run</dt>
            <dd>{formatBool(runResult.dryRun)}</dd>
            <dt>Duration</dt>
            <dd>{runResult.durationMs != null ? `${runResult.durationMs} ms` : "—"}</dd>
            <dt>Categories</dt>
            <dd>
              {runResult.categoriesFetched}/{runResult.categoriesAttempted} fetched
              {runResult.categoriesFailed ? ` (${runResult.categoriesFailed} failed)` : ""}
            </dd>
            <dt>Products parsed</dt>
            <dd>{runResult.productsParsed ?? 0}</dd>
            <dt>Products prepared</dt>
            <dd>{runResult.productsPrepared ?? 0}</dd>
            <dt>Observations inserted</dt>
            <dd>{runResult.observationsInserted ?? 0}</dd>
            <dt>Observations skipped</dt>
            <dd>{runResult.observationsSkipped ?? 0}</dd>
          </dl>

          {runResult.warnings?.length ? (
            <>
              <h3>Warnings</h3>
              <ul>
                {runResult.warnings.map((warning) => (
                  <li key={warning}>{warning}</li>
                ))}
              </ul>
            </>
          ) : null}

          {runResult.errors?.length ? (
            <>
              <h3>Errors</h3>
              <ul>
                {runResult.errors.map((entry, index) => (
                  <li key={`${entry.message || "error"}-${index}`}>
                    {entry.message || JSON.stringify(entry)}
                  </li>
                ))}
              </ul>
            </>
          ) : null}

          {runResult.sampleObservations?.length ? (
            <>
              <h3>Sample observations</h3>
              <AdminTableContainer>
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>Product</th>
                      <th>Brand</th>
                      <th>Price</th>
                      <th>Regular</th>
                      <th>Promo</th>
                      <th>Package</th>
                      <th>Availability</th>
                      <th>Captured</th>
                    </tr>
                  </thead>
                  <tbody>
                    {runResult.sampleObservations.slice(0, 10).map((observation, index) => (
                      <tr key={`${observation.raw_product_name || "product"}-${index}`}>
                        <td>{observation.raw_product_name || "—"}</td>
                        <td>{observation.brand || "—"}</td>
                        <td>{formatCopPrice(observation.price_cop)}</td>
                        <td>{formatCopPrice(observation.regular_price_cop)}</td>
                        <td>{formatCopPrice(observation.promotion_price_cop)}</td>
                        <td>
                          {observation.package_quantity != null
                            ? `${observation.package_quantity} ${observation.package_unit || ""}`.trim()
                            : "—"}
                        </td>
                        <td>{observation.availability_status || "—"}</td>
                        <td>{formatDateTime(observation.captured_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </AdminTableContainer>
            </>
          ) : null}
        </section>
      ) : null}

      <section className="panel-card">
        <div className="food-retail-ingestion-page__section-header">
          <h2>Latest retail price observations</h2>
          <button
            type="button"
            className="btn btn--ghost btn--sm"
            disabled={!selectedSourceKey}
            onClick={() => loadObservations(selectedSourceKey)}
          >
            Refresh
          </button>
        </div>

        {loadingObservations ? <p className="muted">Loading observations…</p> : null}

        {!loadingObservations && observations.length === 0 ? (
          <p className="muted">
            No retail price observations have been collected for this source yet.
          </p>
        ) : null}

        {observations.length ? (
          <AdminTableContainer>
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Product</th>
                  <th>Brand</th>
                  <th>Price</th>
                  <th>Regular</th>
                  <th>Promo</th>
                  <th>Package</th>
                  <th>Availability</th>
                  <th>Store</th>
                  <th>Captured</th>
                  <th>Source URL</th>
                </tr>
              </thead>
              <tbody>
                {observations.map((observation) => (
                  <tr key={observation.id}>
                    <td>{observation.rawProductName || "—"}</td>
                    <td>{observation.brand || "—"}</td>
                    <td>{formatCopPrice(observation.priceCop)}</td>
                    <td>{formatCopPrice(observation.regularPriceCop)}</td>
                    <td>{formatCopPrice(observation.promotionPriceCop)}</td>
                    <td>
                      {observation.packageQuantity != null
                        ? `${observation.packageQuantity} ${observation.packageUnit || ""}`.trim()
                        : "—"}
                    </td>
                    <td>{observation.availabilityStatus || "—"}</td>
                    <td>{observation.storeName || observation.storeBrand || "—"}</td>
                    <td>{formatDateTime(observation.capturedAt)}</td>
                    <td>
                      {observation.sourceUrl ? (
                        <a href={observation.sourceUrl} target="_blank" rel="noreferrer">
                          Link
                        </a>
                      ) : (
                        "—"
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </AdminTableContainer>
        ) : null}
      </section>

      <section className="panel-card food-retail-ingestion-page__normalization">
        <div className="food-retail-ingestion-page__section-header">
          <h2>ARA manual normalization review</h2>
          <a
            className="btn btn--ghost btn--sm"
            href="#/admin/food-ara-normalization-review"
          >
            Open review workspace
          </a>
        </div>
        <p className="muted">
          Resolve ambiguous ARA rows (low confidence, needs review, staple-like NO_MATCH) before
          recalculating the Basic Colombian Household Basket.
        </p>
      </section>

      <FoodRetailNormalizationSection sourceKey={selectedSourceKey || "ARA_COLOMBIA"} />

      {isFoodRetailPlaybookSource(selectedSourceKey) ? (
        <>
          <FoodAraCityGateStrategySection sourceKey={selectedSourceKey} />
          <FoodRetailManualRefreshPlaybookSection sourceKey={selectedSourceKey} />
          <FoodAraFreshnessSection sourceKey={selectedSourceKey} />
          <FoodAraPackageReadinessSection sourceKey={selectedSourceKey} />
          <FoodAraBasketCoverageBaselineSection sourceKey={selectedSourceKey} />
          <FoodAraStapleTargetPlanSection sourceKey={selectedSourceKey} />
          <FoodAraBasketCalculationSection sourceKey={selectedSourceKey} />
        </>
      ) : null}

      {selectedSourceKey === FOOD_SOURCE_KEYS.ARA_COLOMBIA ? (
        <>
          <FoodAraCompleteSustainabilitySection />
          <FoodAraIngestionExpansionSection />
        </>
      ) : null}

      <section className="panel-card">
        <div className="food-retail-ingestion-page__section-header">
          <h2>Recent retail ingestion runs</h2>
          <button
            type="button"
            className="btn btn--ghost btn--sm"
            disabled={!selectedSourceKey}
            onClick={() => loadRuns(selectedSourceKey)}
          >
            Refresh
          </button>
        </div>

        {loadingRuns ? <p className="muted">Loading run history…</p> : null}

        {!loadingRuns && runs.length === 0 ? (
          <p className="muted">No retail ingestion runs recorded for this source yet.</p>
        ) : null}

        {runs.length ? (
          <AdminTableContainer>
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Status</th>
                  <th>Dry run</th>
                  <th>Mock</th>
                  <th>Started</th>
                  <th>Completed</th>
                  <th>Duration</th>
                  <th>Parsed</th>
                  <th>Inserted</th>
                  <th>Skipped</th>
                  <th>Triggered by</th>
                </tr>
              </thead>
              <tbody>
                {runs.map((run) => (
                  <tr key={run.id}>
                    <td>
                      <span className={statusBadgeClass(run.status)}>{run.status}</span>
                    </td>
                    <td>{formatBool(run.dryRun)}</td>
                    <td>{formatBool(run.useMock)}</td>
                    <td>{formatDateTime(run.startedAt)}</td>
                    <td>{formatDateTime(run.completedAt)}</td>
                    <td>{run.durationMs != null ? `${run.durationMs} ms` : "—"}</td>
                    <td>{run.productsParsed ?? 0}</td>
                    <td>{run.observationsInserted ?? 0}</td>
                    <td>{run.observationsSkipped ?? 0}</td>
                    <td>{run.triggeredBy || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </AdminTableContainer>
        ) : null}
      </section>
    </div>
  );
};

export default FoodRetailIngestionPage;
